/* global JQuery */
import { Lang } from '../../Lang';
import { LangTextV2, ModalDialog2, ModalDialogType2 } from 'bambooo';
import QRCode from 'qrcode';
import { QuickConnect } from '../../Api/QuickConnect';
/**
 * QuickConnectModal
 *
 * Renders the QR code the Flutter app scans to log in. Asks the backend for a
 * fresh OTP on every show and on every "Refresh" click; the OTP is single-use
 * and TTL-bound on the server side, so the modal does not have to track expiry
 * any more strictly than a friendly countdown.
 */
export class QuickConnectModal extends ModalDialog2 {
    constructor(elementObject) {
        super({ modalType: ModalDialogType2.small, target: elementObject, id: 'quickconnectmodal' });
        this._countdownInterval = null;
        this._currentExpiresAt = 0;
        this.setTitle(new LangTextV2({ text: 'App Quick Connect' }));
        const body = jQuery('<div class="text-center" />').appendTo(this.getBody());
        jQuery('<p class="text-muted" />')
            .text(Lang.i().l('Scan this code with the MWPA mobile app to sign in.'))
            .appendTo(body);
        const qrWrap = jQuery('<div class="my-3" />').appendTo(body);
        this._qrImg = jQuery('<img alt="Quick Connect QR" />')
            .css({ width: `${QuickConnectModal.QR_PIXEL_SIZE}px`, height: `${QuickConnectModal.QR_PIXEL_SIZE}px` })
            .appendTo(qrWrap);
        this._infoUsername = jQuery('<div class="font-weight-bold" />').appendTo(body);
        this._infoServer = jQuery('<div class="small text-muted" />').appendTo(body);
        this._infoCountdown = jQuery('<div class="small text-muted mt-2" />').appendTo(body);
        this._errorBox = jQuery('<div class="alert alert-danger mt-3" style="display:none;" />').appendTo(body);
        const btnRefresh = this.addButtonSave(Lang.i().l('Refresh'), true);
        btnRefresh.setOnClickFn(async () => {
            await this._loadAndRender();
        });
        this.setOnSave(async () => {
            await this._loadAndRender();
        });
        this.addButtonClose(Lang.i().l('Close'));
        /*
         * Bind directly to the bootstrap event so we don't steal the public
         * setOnHidden slot from callers that want to e.g. destroy() on close.
         */
        this._element.on('hidden.bs.modal', () => {
            this._stopCountdown();
        });
    }
    /**
     * Open the modal and immediately request a fresh OTP. Errors are surfaced
     * in the inline error box rather than as a toast so the user sees them in
     * context (and can hit Refresh to retry).
     */
    async open() {
        this.show();
        await this._loadAndRender();
    }
    async _loadAndRender() {
        this._errorBox.hide().text('');
        this._stopCountdown();
        try {
            const payload = await QuickConnect.generate();
            await this._renderPayload(payload);
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to generate Quick Connect code.';
            this._errorBox.text(msg).show();
            this._qrImg.removeAttr('src');
            this._infoUsername.text('');
            this._infoServer.text('');
            this._infoCountdown.text('');
        }
    }
    async _renderPayload(payload) {
        const dataUrl = await QRCode.toDataURL(JSON.stringify(payload), {
            width: QuickConnectModal.QR_PIXEL_SIZE,
            margin: 1,
            errorCorrectionLevel: 'M'
        });
        this._qrImg.attr('src', dataUrl);
        this._infoUsername.text(payload.username);
        this._infoServer.text(payload.server);
        this._currentExpiresAt = payload.expires_at;
        this._tickCountdown();
        this._countdownInterval = setInterval(() => this._tickCountdown(), 1000);
    }
    _tickCountdown() {
        const remaining = Math.max(0, Math.floor((this._currentExpiresAt - Date.now()) / 1000));
        if (remaining === 0) {
            this._infoCountdown.text(Lang.i().l('Code expired - click Refresh to generate a new one.'));
            this._stopCountdown();
            return;
        }
        this._infoCountdown.text(`Valid for ${remaining}s`);
    }
    _stopCountdown() {
        if (this._countdownInterval !== null) {
            clearInterval(this._countdownInterval);
            this._countdownInterval = null;
        }
    }
}
/**
 * Pixel size of the rendered QR PNG. 280px fits the bootstrap default modal
 * width with comfortable quiet-zone padding on either side.
 */
QuickConnectModal.QR_PIXEL_SIZE = 280;
